import { useCallback, useEffect, useMemo, useState } from 'react';
import { PLATFORM_LABELS, type SubmissionsPerDayRow, type SubmissionsReport } from '@tcg/shared';
import { ApiClientError, adminReportsApi } from '../../lib/api';
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Spinner,
} from '../../components/ui';
import { IconReport } from '../../components/icons';

/**
 * Admin reports: quote and checklist submissions per day over a date range,
 * with quotes split per portal and the hours of manual work they represent.
 *
 * Days are bucketed in the admin's own timezone so "today" lines up with their
 * local clock, and both audit trails have stamped every attempt since the app
 * went live — so any historical range reports real data. Rendered as summary
 * tiles, a stacked bar chart, and a per-day table; no chart dependency, just
 * Tailwind.
 */

/** Local `YYYY-MM-DD` for the browser's timezone (not UTC). */
function toYmd(dt: Date): string {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayYmd(): string {
  return toYmd(new Date());
}

/** Shift a `YYYY-MM-DD` by `n` calendar days (local), returning `YYYY-MM-DD`. */
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return toYmd(new Date(y, m - 1, d + n));
}

/** First day of the month `n` months from the current one. */
function monthStart(n: number): string {
  const now = new Date();
  return toYmd(new Date(now.getFullYear(), now.getMonth() + n, 1));
}

/** Last day of the month `n` months from the current one. */
function monthEnd(n: number): string {
  const now = new Date();
  return toYmd(new Date(now.getFullYear(), now.getMonth() + n + 1, 0));
}

/** "Jul 9" style short label from a `YYYY-MM-DD` (parsed as a local date). */
function shortDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One decimal, grouped — "12.5" reads better than "12.499999999999998". */
function oneDecimal(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

interface Preset {
  label: string;
  range: () => { from: string; to: string };
}

const PRESETS: Preset[] = [
  { label: 'Today', range: () => ({ from: todayYmd(), to: todayYmd() }) },
  { label: 'Last 7 days', range: () => ({ from: addDays(todayYmd(), -6), to: todayYmd() }) },
  { label: 'Last 30 days', range: () => ({ from: addDays(todayYmd(), -29), to: todayYmd() }) },
  { label: 'This month', range: () => ({ from: monthStart(0), to: todayYmd() }) },
  { label: 'Last month', range: () => ({ from: monthStart(-1), to: monthEnd(-1) }) },
];

/**
 * Chart/table colors per portal, in `PLATFORM_IDS` order. A portal the UI
 * doesn't know about (added server-side before this list catches up) falls back
 * to the last entry rather than rendering invisible.
 */
const PORTAL_COLORS = ['bg-accent-500', 'bg-sky-500', 'bg-violet-500', 'bg-amber-500'];

function portalColor(index: number): string {
  return PORTAL_COLORS[index] ?? PORTAL_COLORS[PORTAL_COLORS.length - 1];
}

function portalLabel(id: string): string {
  return PLATFORM_LABELS[id as keyof typeof PLATFORM_LABELS] ?? id;
}

export function Reports() {
  const [from, setFrom] = useState(addDays(todayYmd(), -29));
  const [to, setTo] = useState(todayYmd());
  const [report, setReport] = useState<SubmissionsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangeInvalid = from > to;

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await adminReportsApi.submissions(f, t));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount and whenever a valid range changes.
  useEffect(() => {
    if (rangeInvalid) return;
    void load(from, to);
  }, [from, to, rangeInvalid, load]);

  const applyPreset = (preset: Preset) => {
    const { from: f, to: t } = preset.range();
    setFrom(f);
    setTo(t);
  };

  const activePreset = useMemo(
    () =>
      PRESETS.find((p) => {
        const r = p.range();
        return r.from === from && r.to === to;
      })?.label,
    [from, to],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconReport className="h-5 w-5" />}
        title="Reports"
        subtitle="Quote and checklist submissions per day, and the time they saved."
      />

      <Card>
        <CardHeader title="Date range" subtitle="Days are counted in your local timezone." />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <Field label="From">
                <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
              </Field>
            </div>
            <div className="w-44">
              <Field label="To">
                <Input
                  type="date"
                  value={to}
                  min={from}
                  max={todayYmd()}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-2 pb-0.5">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  variant={activePreset === p.label ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
          {rangeInvalid && (
            <Banner tone="warn">The start date must be on or before the end date.</Banner>
          )}
        </CardBody>
      </Card>

      {error && <Banner tone="error">{error}</Banner>}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400">
          <Spinner />
        </div>
      ) : report ? (
        <ReportView report={report} />
      ) : null}
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = 'slate',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'accent' | 'green' | 'red' | 'slate';
}) {
  const tones = {
    accent: 'text-accent-700',
    green: 'text-emerald-600',
    red: 'text-red-600',
    slate: 'text-slate-900',
  } as const;
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white px-5 py-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/** Small colored square + label, used for both the chart legend and the table. */
function Swatch({ color, children }: { color: string; children: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-slate-500">
      <span className={`h-2.5 w-2.5 rounded-sm ${color}`} />
      {children}
    </span>
  );
}

function ReportView({ report }: { report: SubmissionsReport }) {
  const { rows, totals, hoursSaved, minutesSavedPerQuote } = report;

  // Portal columns come from the server so a newly added portal shows up without
  // a UI change; ordering is stable across the table, chart and legend.
  const portals = useMemo(() => Object.keys(totals.quotesByPortal), [totals.quotesByPortal]);

  const dayCount = rows.length;
  const totalFailed = totals.quotesFailed + totals.checklistsFailed;
  const avgQuotes = dayCount > 0 ? totals.quotes / dayCount : 0;
  const avgChecklists = dayCount > 0 ? totals.checklists / dayCount : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Quote submissions"
          value={totals.quotes.toLocaleString()}
          hint={`${oneDecimal(avgQuotes)} / day avg`}
          tone="accent"
        />
        <StatTile
          label="Checklist submissions"
          value={totals.checklists.toLocaleString()}
          hint={`${oneDecimal(avgChecklists)} / day avg`}
          tone="accent"
        />
        <StatTile
          label="Hours saved"
          value={`${oneDecimal(hoursSaved)} h`}
          hint={`${minutesSavedPerQuote} min per quote`}
          tone="green"
        />
        <StatTile
          label="Failed attempts"
          value={totalFailed.toLocaleString()}
          hint={
            totalFailed > 0
              ? `${totals.quotesFailed} quote / ${totals.checklistsFailed} checklist`
              : 'None in this range'
          }
          tone={totalFailed > 0 ? 'red' : 'slate'}
        />
      </div>

      <Card>
        <CardHeader title="Quote submissions per portal" />
        <CardBody>
          {totals.quotes === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">No quotes in this range.</p>
          ) : (
            <div className="space-y-3">
              {portals.map((portal, i) => {
                const count = totals.quotesByPortal[portal] ?? 0;
                const pct = (count / totals.quotes) * 100;
                return (
                  <div key={portal}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium text-slate-700">{portalLabel(portal)}</span>
                      <span className="tabular-nums text-slate-500">
                        <span className="font-semibold text-slate-900">
                          {count.toLocaleString()}
                        </span>{' '}
                        · {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${portalColor(i)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Submissions per day"
          subtitle="Quotes stacked by portal, checklists alongside."
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            {portals.map((portal, i) => (
              <Swatch key={portal} color={portalColor(i)}>
                {portalLabel(portal)}
              </Swatch>
            ))}
            <Swatch color="bg-slate-400">Checklists</Swatch>
          </div>
          <DayChart rows={rows} portals={portals} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Daily breakdown" />
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3 font-medium">Date</th>
                {portals.map((portal) => (
                  <th key={portal} className="px-4 py-3 text-right font-medium">
                    {portalLabel(portal)}
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-medium">Quotes</th>
                <th className="px-4 py-3 text-right font-medium">Checklists</th>
                <th className="px-5 py-3 text-right font-medium">Hours saved</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.day} className="border-b border-slate-50 last:border-0">
                  <td className="whitespace-nowrap px-5 py-3 text-slate-700">{shortDay(r.day)}</td>
                  {portals.map((portal) => (
                    <td key={portal} className="px-4 py-3 text-right tabular-nums text-slate-500">
                      {r.quotesByPortal[portal] ?? 0}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-900">
                    {r.quotes}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-900">
                    {r.checklists}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-500">
                    {oneDecimal((r.quotes * minutesSavedPerQuote) / 60)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 font-semibold">
                <td className="px-5 py-3 text-slate-700">Total</td>
                {portals.map((portal) => (
                  <td key={portal} className="px-4 py-3 text-right tabular-nums text-slate-700">
                    {totals.quotesByPortal[portal] ?? 0}
                  </td>
                ))}
                <td className="px-4 py-3 text-right tabular-nums text-slate-900">{totals.quotes}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                  {totals.checklists}
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-slate-700">
                  {oneDecimal(hoursSaved)}
                </td>
              </tr>
            </tfoot>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

/** Tallest bar in px; every other bar is scaled against the range's peak day. */
const CHART_HEIGHT = 160;

function DayChart({ rows, portals }: { rows: SubmissionsPerDayRow[]; portals: string[] }) {
  // Scale both series against one peak so the two bars stay comparable.
  const peak = Math.max(1, ...rows.map((r) => Math.max(r.quotes, r.checklists)));

  // A label per column collides past ~2 weeks, so thin them out. Counting back
  // from the end keeps the most recent day labelled, which is the one being read.
  const labelStride = Math.ceil(rows.length / 15);
  const labelled = (i: number) => (rows.length - 1 - i) % labelStride === 0;

  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">No data for this range.</p>;
  }

  return (
    <div
      className="flex items-end gap-1 overflow-x-auto px-1 pb-2"
      style={{ minHeight: CHART_HEIGHT + 40 }}
    >
      {rows.map((r, i) => (
        <div key={r.day} className="flex min-w-[18px] flex-1 flex-col items-center gap-1.5">
          <div
            className="flex w-full items-end justify-center gap-[3px]"
            style={{ height: CHART_HEIGHT }}
          >
            {/* Quotes: one stacked segment per portal, tallest-first from the base. */}
            <div
              className="flex w-full max-w-[18px] flex-col justify-end overflow-hidden rounded-t bg-slate-100"
              style={{ height: Math.max(Math.round((r.quotes / peak) * CHART_HEIGHT), 2) }}
              title={`${shortDay(r.day)} — ${r.quotes} quote${r.quotes === 1 ? '' : 's'}${portals
                .map((p) => `\n  ${portalLabel(p)}: ${r.quotesByPortal[p] ?? 0}`)
                .join('')}`}
            >
              {portals.map((portal, i) => {
                const count = r.quotesByPortal[portal] ?? 0;
                if (count === 0) return null;
                return (
                  <div
                    key={portal}
                    className={portalColor(i)}
                    style={{ height: `${(count / Math.max(r.quotes, 1)) * 100}%` }}
                  />
                );
              })}
            </div>
            {/* Checklists. */}
            <div
              className="w-full max-w-[18px] overflow-hidden rounded-t bg-slate-100"
              style={{ height: Math.max(Math.round((r.checklists / peak) * CHART_HEIGHT), 2) }}
              title={`${shortDay(r.day)} — ${r.checklists} checklist${
                r.checklists === 1 ? '' : 's'
              }`}
            >
              <div className="h-full w-full bg-slate-400" />
            </div>
          </div>
          <span className="h-3 whitespace-nowrap text-[10px] leading-3 text-slate-400">
            {labelled(i) ? shortDay(r.day) : ''}
          </span>
        </div>
      ))}
    </div>
  );
}
